/**
 * Free time windows a resource has on a single day.
 *
 * The hours-based capacity math in `slotEditValidation` says how much of a day
 * is left; this says *when*. A day with 4h left can still be unable to take a
 * 4h booking at 09:00 if the existing booking runs 09:00–13:00, so moving a
 * booking onto a day has to look at the intervals, not only the totals.
 *
 * The gap walking mirrors the per-day free-window splitting in
 * `resourceRequestPlanner` and the backend's `AvailabilityCalculator.ComputeFreeGaps`:
 * bookings that consume capacity are clipped to the day's work window, merged,
 * and the gaps between them are what is left.
 */

import { Booking, WorkingHoursProfile } from '@/types';
import { consumesCapacity } from '@/lib/bookingCapacity';
import { isBookingForResource } from '@/lib/recommendationEngine';

const MINUTES_PER_DAY = 1440;

const PROFILE_DAY_KEYS: (keyof WorkingHoursProfile)[] = [
  'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday',
];

/** Business hours a company works between, when none are configured. */
export const DEFAULT_BUSINESS_HOURS_START = 9;
export const DEFAULT_BUSINESS_HOURS_END = 17;

/** A span of a single day, as minutes since that day's midnight. */
export interface DayInterval {
  startMinutes: number;
  endMinutes: number;
}

/** A free span of a day, with the hours it can take. */
export interface FreeWindow extends DayInterval {
  hours: number;
}

/** A span of a day named by its clock times, "HH:mm" or a timestamp. */
export interface TimeWindow {
  startTime: string;
  endTime: string;
}

/** The "HH:mm" a window boundary reads as, from "HH:mm" or a timestamp. */
function timeOfDay(value: string): string {
  const parsed = /[T ](\d{2}:\d{2})/.exec(value);
  return parsed ? parsed[1] : value;
}

/** Clock-time windows read as minute spans, merged and ordered. */
export function dayIntervalsFromWindows(windows: TimeWindow[]): DayInterval[] {
  const intervals = windows
    .map(window => ({
      startMinutes: hhmmToMinutes(timeOfDay(window.startTime)),
      endMinutes: hhmmToMinutes(timeOfDay(window.endTime)),
    }))
    .filter(interval => interval.endMinutes > interval.startMinutes);
  return mergeIntervals(intervals);
}

/** The parts of `from` that `blocked` does not cover, ordered. */
export function subtractIntervals(
  from: DayInterval[],
  blocked: DayInterval[],
): DayInterval[] {
  const gaps = mergeIntervals(blocked);
  const kept: DayInterval[] = [];

  for (const span of from) {
    let cursor = span.startMinutes;
    for (const gap of gaps) {
      if (gap.endMinutes <= cursor) continue;
      if (gap.startMinutes >= span.endMinutes) break;
      if (gap.startMinutes > cursor) {
        kept.push({ startMinutes: cursor, endMinutes: gap.startMinutes });
      }
      cursor = Math.max(cursor, gap.endMinutes);
      if (cursor >= span.endMinutes) break;
    }
    if (cursor < span.endMinutes) {
      kept.push({ startMinutes: cursor, endMinutes: span.endMinutes });
    }
  }

  return kept;
}

/** The hours two sets of day spans have in common. */
export function overlapHours(a: DayInterval[], b: DayInterval[]): number {
  let minutes = 0;
  for (const first of a) {
    for (const second of b) {
      minutes += Math.max(
        0,
        Math.min(first.endMinutes, second.endMinutes)
          - Math.max(first.startMinutes, second.startMinutes),
      );
    }
  }
  return Math.round((minutes / 60) * 10) / 10;
}

/** "HH:mm" for minutes since midnight. */
export function minutesToHHMM(minutes: number): string {
  const clamped = Math.max(0, Math.min(MINUTES_PER_DAY, Math.round(minutes)));
  const hh = String(Math.floor(clamped / 60)).padStart(2, '0');
  return `${hh}:${String(clamped % 60).padStart(2, '0')}`;
}

/** Minutes since midnight for an "HH:mm" string. */
export function hhmmToMinutes(time: string): number {
  const parsed = /^(\d{1,2}):(\d{2})/.exec(time);
  if (!parsed) return 0;
  return Number(parsed[1]) * 60 + Number(parsed[2]);
}

/** The day and time-of-day a booking timestamp names. */
interface Stamp {
  day: string;
  minutes: number;
}

/**
 * The wall-clock day and time a timestamp reads as.
 *
 * Booking windows are shown and edited as the clock time their timestamp spells
 * out ('2026-07-07T09:00:00Z' is 09:00), so they are read the same way here.
 * Anything not in that shape falls back to its UTC parts, which is the same
 * reading for the timestamps the API returns.
 */
function parseStamp(value: string | undefined | null): Stamp | null {
  if (!value) return null;
  const parsed = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}):(\d{2})/.exec(value);
  if (parsed) {
    return { day: parsed[1], minutes: Number(parsed[2]) * 60 + Number(parsed[3]) };
  }
  const date = new Date(value);
  if (isNaN(date.getTime())) return null;
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return {
    day: `${date.getUTCFullYear()}-${month}-${day}`,
    minutes: date.getUTCHours() * 60 + date.getUTCMinutes(),
  };
}

/** The 'yyyy-MM-dd' a date or day key names. */
function toDayKey(date: Date | string): string | null {
  if (date instanceof Date) {
    if (isNaN(date.getTime())) return null;
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
  }
  const parsed = /^(\d{4}-\d{2}-\d{2})/.exec(date);
  return parsed ? parsed[1] : null;
}

/** A local date for a 'yyyy-MM-dd' key, so its weekday reads correctly. */
function dateForDayKey(key: string): Date | null {
  const parsed = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key);
  if (!parsed) return null;
  return new Date(Number(parsed[1]), Number(parsed[2]) - 1, Number(parsed[3]));
}

/**
 * The part of a day a resource is contracted to work: the company's business
 * hours, shortened to the hours the resource's profile gives that weekday.
 * Null on a day the profile gives no hours at all.
 */
export function dayWorkWindow(
  date: Date | string,
  workingProfile?: WorkingHoursProfile,
  businessHoursStart: number = DEFAULT_BUSINESS_HOURS_START,
  businessHoursEnd: number = DEFAULT_BUSINESS_HOURS_END,
): DayInterval | null {
  const key = toDayKey(date);
  const day = key ? dateForDayKey(key) : null;
  if (!day) return null;

  const businessStart = Math.max(0, Math.min(MINUTES_PER_DAY, businessHoursStart * 60));
  const businessEnd = Math.max(0, Math.min(MINUTES_PER_DAY, businessHoursEnd * 60));
  if (businessEnd <= businessStart) return null;

  const contractedMinutes = workingProfile
    ? (workingProfile[PROFILE_DAY_KEYS[day.getDay()]] ?? 0) * 60
    : businessEnd - businessStart;
  if (!(contractedMinutes > 0)) return null;

  return {
    startMinutes: businessStart,
    endMinutes: Math.min(businessEnd, businessStart + contractedMinutes),
  };
}

/**
 * Spans of a day the resource is already booked for, merged and ordered.
 * Bookings that do not consume capacity (cancelled, completed) are ignored, as
 * they are everywhere else availability is measured.
 */
export function busyIntervalsForDay(
  existingBookings: Booking[],
  resourceId: string,
  date: Date | string,
): DayInterval[] {
  const key = toDayKey(date);
  if (!key) return [];

  const spans: DayInterval[] = [];
  for (const booking of existingBookings) {
    if (!isBookingForResource(booking, resourceId) || !consumesCapacity(booking)) continue;
    const start = parseStamp(booking.startTime);
    const end = parseStamp(booking.endTime);
    if (!start || !end) continue;
    if (end.day < key || start.day > key) continue;
    // A booking that runs past either end of the day covers that end of it.
    const startMinutes = start.day < key ? 0 : start.minutes;
    const endMinutes = end.day > key ? MINUTES_PER_DAY : end.minutes;
    if (endMinutes <= startMinutes) continue;
    spans.push({ startMinutes, endMinutes });
  }

  return mergeIntervals(spans);
}

/** Overlapping or touching spans joined into one, in start order. */
function mergeIntervals(intervals: DayInterval[]): DayInterval[] {
  const sorted = [...intervals].sort((a, b) => a.startMinutes - b.startMinutes);
  const merged: DayInterval[] = [];
  for (const interval of sorted) {
    const last = merged[merged.length - 1];
    if (last && interval.startMinutes <= last.endMinutes) {
      last.endMinutes = Math.max(last.endMinutes, interval.endMinutes);
      continue;
    }
    merged.push({ ...interval });
  }
  return merged;
}

/**
 * The free time windows a resource has on a day, in time order: the gaps its
 * existing bookings leave inside the day's work window.
 *
 * An empty result means the day has nothing to offer — either the profile gives
 * it no hours, or bookings already cover the whole work window.
 */
export function freeWindowsForDay(
  existingBookings: Booking[],
  resourceId: string,
  date: Date | string,
  workingProfile?: WorkingHoursProfile,
  businessHoursStart: number = DEFAULT_BUSINESS_HOURS_START,
  businessHoursEnd: number = DEFAULT_BUSINESS_HOURS_END,
  blockedIntervals: DayInterval[] = [],
): FreeWindow[] {
  const window = dayWorkWindow(date, workingProfile, businessHoursStart, businessHoursEnd);
  if (!window) return [];

  const busy = mergeIntervals([
    ...busyIntervalsForDay(existingBookings, resourceId, date),
    ...blockedIntervals,
  ])
    .map(span => ({
      startMinutes: Math.max(span.startMinutes, window.startMinutes),
      endMinutes: Math.min(span.endMinutes, window.endMinutes),
    }))
    .filter(span => span.endMinutes > span.startMinutes);

  const windows: FreeWindow[] = [];
  const push = (startMinutes: number, endMinutes: number) => {
    if (endMinutes <= startMinutes) return;
    windows.push({
      startMinutes,
      endMinutes,
      hours: Math.round(((endMinutes - startMinutes) / 60) * 10) / 10,
    });
  };

  let cursor = window.startMinutes;
  for (const span of busy) {
    push(cursor, span.startMinutes);
    cursor = Math.max(cursor, span.endMinutes);
  }
  push(cursor, window.endMinutes);

  return windows;
}

/** The free window a span sits entirely inside, if there is one. */
export function containingFreeWindow(
  windows: FreeWindow[],
  startMinutes: number,
  endMinutes: number,
): FreeWindow | null {
  return windows.find(
    w => startMinutes >= w.startMinutes && endMinutes <= w.endMinutes,
  ) ?? null;
}

/** The earliest free window long enough for the given hours, if there is one. */
export function earliestFreeWindowFor(
  windows: FreeWindow[],
  requiredHours: number,
): FreeWindow | null {
  const requiredMinutes = requiredHours * 60;
  return windows.find(w => w.endMinutes - w.startMinutes >= requiredMinutes - 0.5) ?? null;
}

/** Whether a span overlaps any of the resource's bookings on that day. */
export function overlapsBooking(
  busy: DayInterval[],
  startMinutes: number,
  endMinutes: number,
): DayInterval | null {
  return busy.find(
    span => startMinutes < span.endMinutes && endMinutes > span.startMinutes,
  ) ?? null;
}

/**
 * Where a day's booking should sit once it is moved to `date`: the window it
 * already asks for when that is free, otherwise the earliest free window long
 * enough for the hours it needs.
 */
export interface RelocationInput {
  existingBookings: Booking[];
  resourceId: string;
  date: Date | string;
  /** The window the day currently asks for, "HH:mm". */
  startTime: string;
  endTime: string;
  /** Hours the day has to book. Defaults to the current window's length. */
  requiredHours?: number;
  workingProfile?: WorkingHoursProfile;
  businessHoursStart?: number;
  businessHoursEnd?: number;
  /**
   * Spans of the day nothing can be booked into for a reason no booking
   * accounts for — the half day or hours an absence takes.
   */
  blockedIntervals?: DayInterval[];
}

export interface RelocationResult {
  startTime: string;
  endTime: string;
  /** True when the window had to move to clear an existing booking. */
  moved: boolean;
  /**
   * True when no single free window is long enough for the required hours. The
   * window is left as it was asked for, so validation can report it rather than
   * a silently shortened booking being committed.
   */
  noFreeWindow: boolean;
  /** The free windows the day has, for messages that describe them. */
  freeWindows: FreeWindow[];
}

/** A day's share of a total, sitting inside one of the day's free windows. */
export interface FreeWindowAllocation {
  /** 'yyyy-MM-dd'. */
  date: string;
  /** "HH:mm". */
  startTime: string;
  endTime: string;
  hours: number;
}

export interface FreeWindowDistributionInput {
  existingBookings: Booking[];
  resourceId: string;
  /** 'yyyy-MM-dd' days to fill, in the order they should be filled. */
  dates: string[];
  /** Hours to spread across those days. */
  hours: number;
  workingProfile?: WorkingHoursProfile;
  businessHoursStart?: number;
  businessHoursEnd?: number;
  /** The spans of a given day nothing can be booked into (absences). */
  blockedIntervalsFor?: (date: string) => DayInterval[];
  /**
   * The most hours a given day may take. Lets a caller account for hours booked
   * on the day that the client's booking list does not hold — the backend's
   * per-day figures. A day capped at nothing is skipped.
   */
  hoursCapFor?: (date: string) => number;
}

/**
 * Hours spread over a resource's free windows, earliest first: a day's windows
 * are filled in time order and a day is filled before the next is started,
 * which is how the backend-driven plan distribution fills days too.
 *
 * Stops once the hours are placed, and returns only what fitted — a resource
 * whose free time is shorter than the hours asked for gets the days that do fit,
 * so the caller can report the shortfall rather than overbook them.
 */
export function distributeAcrossFreeWindows(
  input: FreeWindowDistributionInput,
): FreeWindowAllocation[] {
  const {
    existingBookings, resourceId, dates, hours,
    workingProfile, businessHoursStart, businessHoursEnd, blockedIntervalsFor,
    hoursCapFor,
  } = input;

  const allocations: FreeWindowAllocation[] = [];
  let remaining = hours;

  for (const date of dates) {
    if (remaining <= 0.05) break;
    let dayRemaining = hoursCapFor ? hoursCapFor(date) : Infinity;
    if (dayRemaining <= 0.05) continue;
    const windows = freeWindowsForDay(
      existingBookings, resourceId, date, workingProfile,
      businessHoursStart, businessHoursEnd, blockedIntervalsFor?.(date) ?? [],
    );

    for (const window of windows) {
      if (remaining <= 0.05 || dayRemaining <= 0.05) break;
      const take = Math.round(Math.min(window.hours, remaining, dayRemaining) * 10) / 10;
      if (take <= 0) continue;
      allocations.push({
        date,
        startTime: minutesToHHMM(window.startMinutes),
        endTime: minutesToHHMM(window.startMinutes + take * 60),
        hours: take,
      });
      remaining = Math.round((remaining - take) * 10) / 10;
      dayRemaining = Math.round((dayRemaining - take) * 10) / 10;
    }
  }

  return allocations;
}

export function relocateDayWindow(input: RelocationInput): RelocationResult {
  const {
    existingBookings, resourceId, date, startTime, endTime,
    workingProfile, businessHoursStart, businessHoursEnd, blockedIntervals,
  } = input;

  const startMinutes = hhmmToMinutes(startTime);
  const endMinutes = hhmmToMinutes(endTime);
  const requestedHours = Math.round(((endMinutes - startMinutes) / 60) * 10) / 10;
  const requiredHours = input.requiredHours && input.requiredHours > 0
    ? input.requiredHours
    : requestedHours;

  const freeWindows = freeWindowsForDay(
    existingBookings, resourceId, date, workingProfile,
    businessHoursStart, businessHoursEnd, blockedIntervals,
  );

  const unchanged = { startTime, endTime, moved: false, noFreeWindow: false, freeWindows };

  // Nothing is known about the day's shape (a non-working day, or no work window
  // at all), so the window is left alone for the capacity checks to judge.
  if (freeWindows.length === 0) return unchanged;
  if (!(requiredHours > 0)) return unchanged;
  if (containingFreeWindow(freeWindows, startMinutes, endMinutes)) return unchanged;

  const target = earliestFreeWindowFor(freeWindows, requiredHours);
  if (!target) return { ...unchanged, noFreeWindow: true };

  return {
    startTime: minutesToHHMM(target.startMinutes),
    endTime: minutesToHHMM(target.startMinutes + requiredHours * 60),
    moved: true,
    noFreeWindow: false,
    freeWindows,
  };
}
